import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Label, Modal, ModalBody, ModalHeader } from "reactstrap";
import { BsEye } from "react-icons/bs";
import { FaPencilAlt, FaSearch, FaTrash } from "react-icons/fa";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import Breadcrumbs from "../../Component/Common/Breadcrumbs";
import ConfirmModal from "../../Component/Common/ConstomModal/ConfirmModal";
import CustomPagination from "../../Component/Common/CustomPagination";
import { CustomReactTable } from "../../Component/Common/CustomReactTable";
import Dropdown from "../../Component/Common/Dropdown";
import { InputField } from "../../Component/Common/InputField";
import useDebounce from "../../helper/customhook";
import { handleKeyDown, FEATURES, hasAccess, getAccessLevel, ACCESS } from "../../helper";
import { getStorage } from "../../helper/storage";
import { deleteBlaBle, getBlaBleDetails, getBlaBleList } from "../../services/blaBleService";
import UserContext from "../../helper/UserContext";
import "../BlaBle/BlaBle.css";

const PAGE_SIZE = 20;
// When a filter is active and the backend appears to ignore the filter
// params (i.e. it keeps returning the same count as the unfiltered list),
// fall back to fetching a much larger page and filtering client-side.
const FALLBACK_FILTER_PAGE_SIZE = 1000;
const PLACEHOLDER_AVATAR =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Crect width='160' height='160' fill='%23eef2f7'/%3E%3Ccircle cx='80' cy='61' r='31' fill='%2395a3b8'/%3E%3Cpath d='M28 139c8-32 29-49 52-49s44 17 52 49' fill='%2395a3b8'/%3E%3C/svg%3E";

const basicFields = [
  { name: "name", label: "Name" },
  { name: "mobile", label: "Mobile Number" },
  { name: "epic_no", label: "EPIC Number" },
  { name: "assembly_no", label: "Assembly Number" },
  { name: "booth_no", label: "Booth Number" },
];
const extraFields = Array.from({ length: 10 }, (_, index) => ({ name: `extra${index + 1}`, label: `Extra ${index + 1}` }));
const descriptionFields = Array.from({ length: 5 }, (_, index) => ({ name: `description${index + 1}`, label: `Description ${index + 1}` }));

const VIEW_SECTIONS = [
  { id: "basic", label: "Basic Information", fields: basicFields },
  { id: "extra", label: "Extra Information", fields: extraFields },
  { id: "description", label: "Description Information", fields: descriptionFields },
];

const TYPE_OPTIONS = [
  { label: "BLA", value: "BLA" },
  { label: "BLE", value: "BLE" },
];
const FILTER_KEYS = ["name", "rel_name", "mobile", "type"];

const getRecordId = (item) => item?.uuid || item?.id;
const getImageUrl = (item) => item?.profile_image || item?.profile_image_url || item?.image || item?.image_url || "";
const valueOrDash = (value) => (value === null || value === undefined || value === "" ? "-" : value);
const getAssemblyNumbers = (userLogin, user) => user?.appDetails?.assembly_number || userLogin?.appDetails?.assembly_number || [];

const normalizeRecord = (item = {}) => ({
  ...item,
  mobile: item?.mobile || item?.mobile_number || item?.mobile_no || "",
  epic_no: item?.epic_no || item?.epic_number || item?.epic || "",
  assembly_no: item?.assembly_no || item?.assembly_number || "",
  booth_no: item?.booth_no || item?.booth_number || "",
  type: item?.type || "BLA",
  gender: item?.gender || "",
  rel_name: item?.rel_name || item?.relative_name || "",
  party_name: item?.party_name || "",
  address: item?.address || "",
  profile_image: getImageUrl(item),
  is_active: item?.is_active === undefined || item?.is_active === null ? item?.status !== false : item?.is_active,
});

// Applies the same filters we send to the API, but on the frontend. Used as
// a fallback when the backend doesn't actually support these query params.
const applyClientFilters = (records, filterValues) => {
  const name = filterValues?.name?.trim().toLowerCase();
  const relName = filterValues?.rel_name?.trim().toLowerCase();
  const mobile = filterValues?.mobile?.trim();
  const type = filterValues?.type?.trim().toLowerCase();
  if (!name && !relName && !mobile && !type) return records;
  return records.filter((item) => {
    const record = normalizeRecord(item);
    if (name && !String(record.name || "").toLowerCase().includes(name)) return false;
    if (relName && !String(record.rel_name || "").toLowerCase().includes(relName)) return false;
    if (mobile && !String(record.mobile || "").includes(mobile)) return false;
    if (type && String(record.type || "").toLowerCase() !== type) return false;
    return true;
  });
};

// Booth Agent Management list (was BlaBle.js) - "Add Booth Agent" (create +
// edit) moved to its own routed page; View stays here as a lightweight
// inline modal since it's a distinct read-only detail card, not the edit
// form. Preserves the client-side filter-fallback mechanism exactly - the
// backend intermittently doesn't apply name/rel_name/mobile/type filter
// params, detected here by comparing a filtered fetch's count against an
// unfiltered baseline for the same assembly.
export const BoothAgentList = () => {
  const navigate = useNavigate();
  const userLogin = getStorage("logged-in");
  const { user } = useContext(UserContext);
  const queryClient = useQueryClient();

  const [openDel, setOpenDel] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState(PLACEHOLDER_AVATAR);
  const [selectedAssembly, setSelectedAssembly] = useState(null);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState(null);

  const avfBodyRef = useRef(null);
  const avfSectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState(VIEW_SECTIONS[0].id);
  const avfScrollLockRef = useRef(false);
  const avfScrollLockTimeoutRef = useRef(null);

  useEffect(() => () => window.clearTimeout(avfScrollLockTimeoutRef.current), []);

  const registerSectionRef = (id) => (el) => {
    avfSectionRefs.current[id] = el;
  };

  const scrollToSection = (id) => {
    const container = avfBodyRef.current;
    const target = avfSectionRefs.current[id];
    if (!container || !target) return;
    avfScrollLockRef.current = true;
    window.clearTimeout(avfScrollLockTimeoutRef.current);
    avfScrollLockTimeoutRef.current = window.setTimeout(() => {
      avfScrollLockRef.current = false;
    }, 600);
    container.scrollTo({ top: target.offsetTop - 8, behavior: "smooth" });
    setActiveSection(id);
  };

  const handleAvfBodyScroll = () => {
    if (avfScrollLockRef.current) return;
    const container = avfBodyRef.current;
    if (!container) return;
    const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 4;
    if (atBottom) {
      setActiveSection(VIEW_SECTIONS[VIEW_SECTIONS.length - 1].id);
      return;
    }
    const scrollPos = container.scrollTop + 48;
    let current = VIEW_SECTIONS[0].id;
    VIEW_SECTIONS.forEach((section) => {
      const target = avfSectionRefs.current[section.id];
      if (target && target.offsetTop <= scrollPos) current = section.id;
    });
    setActiveSection(current);
  };
  const [backendFiltersUnsupported, setBackendFiltersUnsupported] = useState(false);
  const [filter, setFilter] = useState({
    limit: PAGE_SIZE,
    offset: 0,
    currentPage: 0,
    search: "",
    ordering: "-id",
    ac_no: "",
    name: "",
    rel_name: "",
    mobile: "",
    type: "",
    app_name: userLogin?.app_name,
    party_name: userLogin?.party_name,
  });
  const debouncedFilter = useDebounce(filter, 500);

  const assemblyOptions = useMemo(
    () =>
      getAssemblyNumbers(userLogin, user)
        .filter(Boolean)
        .map((assemblyNumber) => ({ label: String(assemblyNumber), value: String(assemblyNumber) })),
    [userLogin, user]
  );

  const hasRequiredListParams = Boolean(debouncedFilter?.app_name && debouncedFilter?.party_name && debouncedFilter?.ac_no);
  const listParamError = !filter?.app_name
    ? "App name is required."
    : !filter?.party_name
    ? "Party name is required."
    : !filter?.ac_no
    ? "Please select an assembly number to load Booth Agent records."
    : "";

  const hasActiveFilter = FILTER_KEYS.some((key) => Boolean(filter?.[key]));

  const effectiveFilter = useMemo(() => {
    if (hasActiveFilter && backendFiltersUnsupported) {
      return { ...debouncedFilter, limit: FALLBACK_FILTER_PAGE_SIZE, offset: 0 };
    }
    return debouncedFilter;
  }, [debouncedFilter, hasActiveFilter, backendFiltersUnsupported]);

  const { data, isLoading, isFetching } = useQuery(["getBlaBleList", effectiveFilter], () => getBlaBleList(effectiveFilter), {
    enabled: hasRequiredListParams,
  });

  const baselineFilter = useMemo(
    () => ({
      limit: 1,
      offset: 0,
      ordering: "-id",
      ac_no: debouncedFilter?.ac_no,
      app_name: debouncedFilter?.app_name,
      party_name: debouncedFilter?.party_name,
    }),
    [debouncedFilter?.ac_no, debouncedFilter?.app_name, debouncedFilter?.party_name]
  );

  const { data: baselineData } = useQuery(["getBlaBleListBaseline", baselineFilter], () => getBlaBleList(baselineFilter), {
    enabled: hasRequiredListParams && hasActiveFilter && !backendFiltersUnsupported,
  });

  useEffect(() => {
    if (
      hasActiveFilter &&
      !backendFiltersUnsupported &&
      typeof data?.count === "number" &&
      typeof baselineData?.count === "number" &&
      data.count === baselineData.count &&
      baselineData.count > 0
    ) {
      setBackendFiltersUnsupported(true);
    }
  }, [hasActiveFilter, backendFiltersUnsupported, data?.count, baselineData?.count]);

  useEffect(() => {
    if (!hasActiveFilter && backendFiltersUnsupported) setBackendFiltersUnsupported(false);
  }, [hasActiveFilter, backendFiltersUnsupported]);

  const rawList = useMemo(() => data?.results || [], [data]);
  const list = useMemo(() => {
    if (hasActiveFilter && backendFiltersUnsupported) return applyClientFilters(rawList, filter);
    return rawList;
  }, [rawList, hasActiveFilter, backendFiltersUnsupported, filter]);

  const effectiveCount = hasActiveFilter && backendFiltersUnsupported ? list.length : data?.count || 0;
  const effectiveLimit = hasActiveFilter && backendFiltersUnsupported ? list.length || 1 : filter.limit;

  useEffect(() => {
    if (!filter.ac_no && assemblyOptions.length) {
      const firstAssembly = assemblyOptions[0];
      setSelectedAssembly(firstAssembly);
      setFilter((current) => ({ ...current, ac_no: firstAssembly.value, offset: 0, currentPage: 0 }));
    }
  }, [assemblyOptions, filter.ac_no]);

  const deleteMutation = useMutation(deleteBlaBle, {
    onSuccess: (res) => {
      if (res?.success) {
        toast.success(res?.message || "success");
        queryClient.invalidateQueries("getBlaBleList");
        setOpenDel(false);
        setDeleteId("");
      }
    },
  });

  const handleViewAction = async (item) => {
    const id = getRecordId(item);
    setSelectedRecord(normalizeRecord(item));
    setIsViewOpen(true);
    setActiveSection(VIEW_SECTIONS[0].id);
    if (id) {
      const res = await getBlaBleDetails({ id });
      if (res?.success && res?.results) setSelectedRecord(normalizeRecord(res.results));
    }
  };

  const handlePageClick = (data) => {
    const currentPage = data.selected;
    setFilter((current) => ({ ...current, offset: currentPage * current.limit, currentPage }));
  };

  const updateSearch = ({ target: { value } }) => {
    setFilter((current) => ({ ...current, search: value, offset: 0, currentPage: 0 }));
  };

  const updateListFilter = (values) => {
    setFilter((current) => ({ ...current, ...values, offset: 0, currentPage: 0 }));
  };

  const handleClearFilters = () => {
    setSelectedTypeFilter(null);
    updateListFilter({ name: "", rel_name: "", mobile: "", type: "" });
  };

  const handleAssemblyFilterChange = (option) => {
    setSelectedAssembly(option);
    setFilter((current) => ({ ...current, ac_no: option?.value || "", offset: 0, currentPage: 0 }));
  };

  const columns = [
    { Header: "Profile Picture", accessor: "profile_image" },
    { Header: "Name", accessor: "name" },
    { Header: "Relative Name", accessor: "rel_name" },
    { Header: "Mobile", accessor: "mobile" },
    { Header: "EPIC No", accessor: "epic_no" },
    { Header: "Assembly No", accessor: "assembly_no" },
    { Header: "Booth No", accessor: "booth_no" },
    { Header: "Type", accessor: "type" },
    { Header: "Gender", accessor: "gender" },
    { Header: "Party Name", accessor: "party_name" },
    { Header: "Address", accessor: "address" },
    { Header: "Status", accessor: "status" },
    { Header: "Action", accessor: "action" },
  ];

  const rowDataList = (dataList) =>
    dataList.map((item) => {
      const record = normalizeRecord(item);
      const id = getRecordId(record);
      return {
        ...record,
        profile_image: (
          <img
            className="bla-ble-avatar"
            src={record.profile_image || PLACEHOLDER_AVATAR}
            alt={record.name || "Booth Agent"}
            onError={({ currentTarget }) => {
              currentTarget.src = PLACEHOLDER_AVATAR;
            }}
          />
        ),
        name: valueOrDash(record.name),
        mobile: valueOrDash(record.mobile),
        epic_no: valueOrDash(record.epic_no),
        assembly_no: valueOrDash(record.assembly_no),
        booth_no: valueOrDash(record.booth_no),
        type: valueOrDash(record.type || "BLA"),
        gender: valueOrDash(record.gender),
        rel_name: valueOrDash(record.rel_name),
        party_name: valueOrDash(record.party_name || userLogin?.party_name),
        address: valueOrDash(record.address),
        status: (
          <span className={`bla-ble-status ${record.is_active ? "" : "inactive"}`}>{record.is_active ? "Active" : "Inactive"}</span>
        ),
        action: (
          <div className="bla-ble-actions">
            <button type="button" className="bla-ble-action-btn" onClick={() => handleViewAction(record)}>
              <BsEye /> View
            </button>
            {hasAccess(userLogin?.user_type, FEATURES.BLA_BLE, ACCESS.LIMITED) && (
              <button
                type="button"
                className="bla-ble-action-btn"
                onClick={() => navigate(`/booth-agent-management/add?editId=${id}`)}
              >
                <FaPencilAlt /> Edit
              </button>
            )}
            {getAccessLevel(userLogin?.user_type, FEATURES.BLA_BLE) === ACCESS.FULL && (
              <button
                type="button"
                className="bla-ble-action-btn delete"
                onClick={() => {
                  setDeleteId(id);
                  setOpenDel(true);
                }}
              >
                <FaTrash /> Delete
              </button>
            )}
          </div>
        ),
      };
    });

  return (
    <>
      <div className="ques_container bla-ble-page">
        {/* <Breadcrumbs
          active="Booth Agent"
          action={
            hasAccess(userLogin?.user_type, FEATURES.BLA_BLE, ACCESS.LIMITED) ? (
              <button className="addPlan" onClick={() => navigate("/booth-agent-management/add")}>
                Add Booth Agent
              </button>
            ) : null
          }
        /> */}

        <div className="bla-ble-toolbar">
          <div className="bla-ble-filter">
            <Label className="modal_label">Assembly Number</Label>
            <Dropdown
              placeholder="Select Assembly Number"
              options={assemblyOptions}
              valueText="value"
              labelText="label"
              selectedOption={selectedAssembly}
              setSelectedOption={handleAssemblyFilterChange}
              classNamePrefix="anvRc_controller"
              isClearable
            />
          </div>
          <div className="bla-ble-search">
            <InputField className="anvSearch_input" placeholder="Search" name="search" value={filter.search} preAddon={<FaSearch />} onChange={updateSearch} />
          </div>
        </div>

        <div className="bla-ble-toolbar2 bla-ble-advanced-filters">
          <div className="bla-ble-filter">
            <Label className="modal_label">First Name</Label>
            <InputField className="anvSearch_input" placeholder="First Name" name="name" value={filter.name} onChange={(e) => updateListFilter({ name: e.target.value })} />
          </div>
          <div className="bla-ble-filter">
            <Label className="modal_label">Last Name</Label>
            <InputField
              className="anvSearch_input"
              placeholder="Last Name (Relative)"
              name="rel_name"
              value={filter.rel_name}
              onChange={(e) => updateListFilter({ rel_name: e.target.value })}
            />
          </div>
          <div className="bla-ble-filter">
            <Label className="modal_label">Mobile Number</Label>
            <InputField
              className="anvSearch_input"
              placeholder="Mobile Number"
              name="mobile"
              maxLength={10}
              onKeyDown={handleKeyDown}
              value={filter.mobile}
              onChange={(e) => updateListFilter({ mobile: e.target.value })}
            />
          </div>
          <div className="bla-ble-filter2">
            <Label className="modal_label">Type</Label>
            <Dropdown
              placeholder="Select Type"
              options={TYPE_OPTIONS}
              valueText="value"
              labelText="label"
              isClearable
              selectedOption={selectedTypeFilter}
              setSelectedOption={(option) => {
                setSelectedTypeFilter(option);
                updateListFilter({ type: option?.value || "" });
              }}
              classNamePrefix="anvRc_controller"
            />
          </div>
          {hasActiveFilter && (
            <div className="bla-ble-filter bla-ble-clear-filter">
              <button type="button" className="mobile-visibility-toggle" onClick={handleClearFilters}>
                <span>Clear Filters</span>
              </button>
            </div>
          )}
        </div>

        {listParamError && <div className="bla-ble-alert">{listParamError}</div>}

        <CustomReactTable
          columns={columns}
          data={rowDataList(list)}
          tableLoading={hasRequiredListParams && (isLoading || isFetching)}
          containerClassName="bla-ble-table-wrap"
          tableClassName="bla-ble-table"
        />
      </div>

      {list.length > 0 && (
        <CustomPagination onPageChange={handlePageClick} currentPage={filter.currentPage} pageCount={Math.ceil(effectiveCount / effectiveLimit)} />
      )}

      <Modal isOpen={isViewOpen} toggle={() => setIsViewOpen(!isViewOpen)} centered className="avf-dialog" contentClassName="avf-content">
        <div className="avf-header">
          <div className="avf-header-top">
            <div>
              <h2 className="avf-title">View Booth Agent</h2>
              <p className="avf-subtitle">Booth agent details (read-only)</p>
            </div>
            <button type="button" className="avf-close-btn" onClick={() => setIsViewOpen(false)} aria-label="Close">
              ×
            </button>
          </div>
          <div className="avf-rail">
            {VIEW_SECTIONS.map((section, index) => (
              <button
                key={section.id}
                type="button"
                className={`avf-step ${activeSection === section.id ? "is-active" : ""}`}
                onClick={() => scrollToSection(section.id)}
              >
                <span className="avf-step-num">{index + 1}</span>
                <span className="avf-step-label">{section.label}</span>
              </button>
            ))}
          </div>
        </div>

        {selectedRecord && (
          <div className="avf-body" ref={avfBodyRef} onScroll={handleAvfBodyScroll}>
            <div className="avf-photo-card">
              <button
                type="button"
                className="avf-photo-avatar"
                onClick={() => {
                  if (selectedRecord.profile_image) {
                    setPreviewImageSrc(selectedRecord.profile_image);
                    setPreviewModalOpen(true);
                  }
                }}
                disabled={!selectedRecord.profile_image}
                title={selectedRecord.profile_image ? "View full photo" : undefined}
              >
                <img
                  src={selectedRecord.profile_image || PLACEHOLDER_AVATAR}
                  alt={selectedRecord.name || "Booth Agent"}
                  onError={({ currentTarget }) => {
                    currentTarget.src = PLACEHOLDER_AVATAR;
                  }}
                />
              </button>
              <div className="avf-photo-info">
                <div className="avf-photo-title">{valueOrDash(selectedRecord.name)}</div>
                <div className="avf-photo-subtitle">{valueOrDash(selectedRecord.mobile)}</div>
              </div>
            </div>

            {VIEW_SECTIONS.map((section, index) => (
              <div key={section.id} className="avf-section" ref={registerSectionRef(section.id)}>
                <div className="avf-section-head">
                  <span className="avf-section-num">{index + 1}</span>
                  <h3 className="avf-section-title">{section.label}</h3>
                </div>
                <div className="avf-grid">
                  {section.fields.map((field) => (
                    <div className="avf-field" key={field.name}>
                      <InputField
                        floating
                        disabled
                        className="modal_input"
                        label={field.label}
                        labelClass="modal_label"
                        name={field.name}
                        value={valueOrDash(selectedRecord?.[field.name])}
                        onChange={() => {}}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="avf-footer">
          <div className="avf-footer-note">Read-only record</div>
          <div className="avf-footer-actions">
            <button type="button" className="avf-btn avf-btn-primary" onClick={() => setIsViewOpen(false)}>
              Close
            </button>
          </div>
        </div>
      </Modal>

      <Modal size="sm" isOpen={previewModalOpen} toggle={() => setPreviewModalOpen(false)} centered>
        <ModalHeader toggle={() => setPreviewModalOpen(false)}>Profile Photo</ModalHeader>
        <ModalBody style={{ textAlign: "center" }}>
          <img
            src={previewImageSrc || PLACEHOLDER_AVATAR}
            alt="Profile full preview"
            style={{ maxWidth: "100%", borderRadius: 8 }}
            onError={({ currentTarget }) => {
              currentTarget.src = PLACEHOLDER_AVATAR;
            }}
          />
        </ModalBody>
      </Modal>

      <ConfirmModal
        isOpen={openDel}
        toggle={() => {
          setOpenDel(!openDel);
          setDeleteId("");
        }}
        message="Are you sure you want to delete this record?"
        submitLabel="Delete"
        onSubmit={() => deleteMutation.mutate({ id: deleteId })}
      />
    </>
  );
};
